import { useEffect, useRef, useState } from 'react';
import {
  Alert, Badge, Box, Button, Center, Code, CopyButton, Drawer, Group, Loader, ScrollArea, SimpleGrid, Stack, Table, Text, Tooltip,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconCheck, IconCopy, IconFileText, IconPlayerPause, IconPlayerPlay, IconRefresh, IconRotateClockwise,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDateTime } from '../lib/format';
import { SILENT_ERROR } from '../lib/queryMeta';
import { useServerSoati } from '../hooks/useServerSoati';
import {
  agentHolati, agentXatoKaliti, holatMatni, logKutilmoqdami, oxirgiAloqa, oxirgiLogBuyrugi, qisqaMatn,
  BUYRUQ_RANG, LOG_KUTISH_MS,
  type AgentBatafsil, type AgentBuyruq, type AgentYollari, type BuyruqTur,
} from '../lib/agent';

/** Tasdiq oynasi bilan beriladigan buyruqlar (spec §6.2) → oynadagi izoh kaliti. */
const TASDIQ: Partial<Record<BuyruqTur, string>> = {
  QAYTA_ISHGA_TUSHIR: 'agent.tasdiqQayta',
  TOXTAT: 'agent.tasdiqToxtat',
};

/** Jadval ichidagi belgi kesilmasin: telefonda ustunlar torayganda Badge "B…" bo'lib qolardi (AgentJadvali bilan bir xil). */
const BADGE_TOLIQ = { root: { overflow: 'visible', maxWidth: 'none' }, label: { overflow: 'visible', textOverflow: 'clip' } } as const;

/** Jadvaldagi qisqa matn; to'liq matn tooltipda. */
function QisqaMatn({ v, max, rang }: { v: unknown; max: number; rang?: string }) {
  const toliq = qisqaMatn(v, 2000);
  if (!toliq) return <Text size="xs" c="dimmed">—</Text>;
  return (
    <Tooltip label={toliq} disabled={toliq.length <= max} withArrow multiline w={340} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      <Text size="xs" c={rang ?? 'dimmed'}>{qisqaMatn(v, max)}</Text>
    </Tooltip>
  );
}

function Maydon({ nom, qiymat, mono }: { nom: string; qiymat: string; mono?: boolean }) {
  return (
    <Box>
      <Text size="xs" c="dimmed">{nom}</Text>
      <Text size="sm" ff={mono ? 'monospace' : undefined} style={{ wordBreak: 'break-word' }}>{qiymat}</Text>
    </Box>
  );
}

/**
 * FACE ID agenti (maktab kompyuteri) — yon oyna: holat, buyruq tugmalari, oxirgi vazifalar,
 * buyruqlar tarixi va log (spec 2026-10-15 agent boshqaruv §6.2). Maktab FACE ID sahifasi va
 * superadmin "FACE ID agentlar" sahifasi uchun BITTA komponent — farqi `yol` va huquqda.
 * Haqiqiy huquq tekshiruvi serverda (buyruq — faqat STAFF / superadmin).
 */
export function AgentOynasi({
  agentId, onClose, yol, buyruqBera, maktabUstuni = false,
}: {
  /** null — oyna yopiq (so'rovlar ham to'xtaydi). */
  agentId: string | null;
  onClose: () => void;
  yol: AgentYollari;
  /** Buyruq tugmalari (STAFF / superadmin); buxgalter faqat ko'radi. */
  buyruqBera: boolean;
  /** Superadmin: maktab nomi ham ko'rsatiladi. */
  maktabUstuni?: boolean;
}) {
  const { t } = useTranslation();
  const tt = (k: string, o?: { n: number }) => t(k, o);
  const qc = useQueryClient();
  const tor = useMediaQuery('(max-width: 48em)');

  // Yopilish animatsiyasi paytida oxirgi agent ko'rinib tursin (so'rovsiz — `enabled` faqat ochiqda)
  const oxirgiId = useRef(agentId);
  if (agentId) oxirgiId.current = agentId;
  const id = agentId ?? oxirgiId.current;

  // "Logni olish" bosilgan buyruq: u tugaguncha batafsil 2 s da yangilanadi, ko'pi bilan 60 s (§6.2)
  const [logKutish, setLogKutish] = useState<{ id: string; gacha: number } | null>(null);
  useEffect(() => setLogKutish(null), [agentId]);

  const { data, isError, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['agent-batafsil', id ? yol.batafsil(id) : null],
    // Oyna yopiq — so'rov ham, 5 s / 2 s lik takrorlash ham yo'q
    enabled: !!agentId,
    queryFn: async () => (await api.get<AgentBatafsil>(yol.batafsil(id!))).data,
    refetchInterval: logKutish ? 2_000 : 5_000,
    staleTime: 0,
    // Fonda yangilanmaydi — qaytganda darhol yangilansin (belgi eski ma'lumot bilan qizarmasin, useServerSoati)
    refetchOnWindowFocus: true,
    // Xato oynaning o'zida ko'rsatiladi (pastda) — 5 s lik takrorlashda toast yog'ilmasin
    meta: SILENT_ERROR,
  });
  // Holat belgisi server soati bo'yicha (Review Focus 2); 15 s da va har yangi javob bilan qayta hisoblanadi
  const hozir = useServerSoati(dataUpdatedAt);

  const buyruqlar = data?.buyruqlar;
  useEffect(() => {
    if (!logKutish) return;
    const qoldi = logKutish.gacha - Date.now();
    if (qoldi <= 0 || !logKutilmoqdami(buyruqlar ?? [], logKutish.id)) {
      setLogKutish(null);
      return;
    }
    const taymer = setTimeout(() => setLogKutish(null), qoldi);
    return () => clearTimeout(taymer);
  }, [logKutish, buyruqlar]);

  const logId = oxirgiLogBuyrugi(buyruqlar ?? []);
  const { data: logData, isFetching: logYuklanmoqda } = useQuery({
    queryKey: ['agent-log', logId ? yol.log(logId) : null],
    enabled: !!agentId && !!logId,
    queryFn: async () => (await api.get<{ log: string | null }>(yol.log(logId!))).data,
    staleTime: Infinity, // tugagan buyruqning logi o'zgarmaydi
  });

  const buyruq = useMutation({
    mutationFn: async (tur: BuyruqTur) =>
      (await api.post<{ buyruq: AgentBuyruq }>(yol.buyruq(agentId!), { tur })).data.buyruq,
    onSuccess: (b) => {
      notifications.show({ color: 'green', message: t('agent.buyruqYuborildi') });
      if (b.tur === 'LOG') setLogKutish({ id: b.id, gacha: Date.now() + LOG_KUTISH_MS });
      qc.invalidateQueries({ queryKey: ['agent-batafsil'] });
    },
    onError: (e: unknown) => {
      // spec §6.2: 409 takror → i18n; qolgani (400 eskiAgent ham) — serverning `error` matni, bo'lmasa umumiy xato
      const r = (e as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
      const k = agentXatoKaliti(r?.data);
      notifications.show({
        color: r?.status === 409 ? 'yellow' : 'red',
        message: k ? t(k) : String(r?.data?.error ?? t('common.error')),
      });
    },
  });

  const a = data?.agent;
  const h = a ? agentHolati(a, hozir) : null;
  const eski = !!a && !a.holatAt;
  // Superadmin: maktabga bog'lanmagan terminal (tenant null) — server buyruqni rad etadi, tugmalar ko'rsatilmaydi
  const maktabsiz = maktabUstuni && !!a && !a.tenant;

  const bos = (tur: BuyruqTur) => {
    const izoh = TASDIQ[tur];
    if (!izoh || !a) return buyruq.mutate(tur);
    modals.openConfirmModal({
      title: t('agent.tasdiqSarlavha'),
      centered: true,
      zIndex: 400, // yon oyna ustida
      children: <Text size="sm"><b>{a.name}</b> — {t(izoh)}</Text>,
      labels: { confirm: t(`agent.tur.${tur}`), cancel: t('common.cancel') },
      confirmProps: { color: tur === 'TOXTAT' ? 'orange' : 'red' },
      onConfirm: () => buyruq.mutate(tur),
    });
  };

  const tugma = (tur: BuyruqTur, icon: React.ReactNode, rang: string) => (
    <Button
      size="xs"
      variant="light"
      color={rang}
      leftSection={icon}
      loading={buyruq.isPending && buyruq.variables === tur}
      disabled={buyruq.isPending && buyruq.variables !== tur}
      onClick={() => bos(tur)}
    >
      {t(`agent.tur.${tur}`)}
    </Button>
  );

  return (
    <Drawer
      opened={!!agentId}
      onClose={onClose}
      position="right"
      size={tor ? '100%' : 'xl'}
      title={
        <Box>
          <Text fw={700}>{a?.name ?? '…'}</Text>
          {maktabUstuni && a?.tenant && <Text size="xs" c="dimmed">{a.tenant.name}</Text>}
        </Box>
      }
    >
      {/* Qayta so'rov yiqilsa ham (eski ma'lumot bor bo'lsa ham) — ochiq xato + "Qayta urinish": eski ma'lumot qolib, "hozir"
          siljiyversa ~2 daqiqada belgi yolg'on "Aloqa yo'q" bo'lardi (superadmin "FACE ID agentlar" naqshi) */}
      {isError ? (
        <Stack align="center" gap="sm">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} title={t('common.error')} w="100%">
            {t('agent.yuklanmadi')}
          </Alert>
          <Button variant="light" loading={isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
            {t('agent.qaytaUrinish')}
          </Button>
        </Stack>
      ) : !a || !h || !data ? (
        <Center py="xl"><Loader /></Center>
      ) : (
        <Stack gap="lg">
          {/* Holat */}
          <Stack gap="xs">
            <Group gap="xs">
              <Badge color={h.rang} variant="light" size="lg">{holatMatni(h, tt)}</Badge>
              {a.holat?.band && <Badge color="blue" variant="outline">{t('agent.band')}</Badge>}
            </Group>
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="xs" verticalSpacing="xs">
              {maktabUstuni && <Maydon nom={t('agent.maktab')} qiymat={a.tenant?.name ?? '—'} />}
              <Maydon
                nom={t('agent.terminal')}
                qiymat={a.holat?.terminalManzil ? `${a.terminal.name} · ${a.holat.terminalManzil}` : a.terminal.name}
              />
              <Maydon nom={t('agent.ip')} qiymat={a.ip ?? '—'} mono />
              <Maydon nom={t('agent.versiya')} qiymat={a.version ?? '—'} mono />
              <Maydon nom={t('agent.os')} qiymat={a.holat?.os ?? '—'} />
              {/* Kompyuter soati bo'yicha — faqat ko'rsatish uchun */}
              <Maydon nom={t('agent.ishgaTushgan')} qiymat={formatDateTime(a.holat?.ishgaTushgan)} />
              <Maydon nom={t('agent.oxirgiAloqa')} qiymat={formatDateTime(oxirgiAloqa(a))} />
            </SimpleGrid>
            {a.holat?.oxirgiXato && (
              <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} title={t('agent.oxirgiXato')}>
                <Text size="sm" style={{ wordBreak: 'break-word' }}>{a.holat.oxirgiXato}</Text>
                {a.holat.oxirgiXatoAt && <Text size="xs" c="dimmed" mt={4}>{formatDateTime(a.holat.oxirgiXatoAt)}</Text>}
              </Alert>
            )}
          </Stack>

          {/* Buyruqlar */}
          {eski ? (
            <Alert color="gray" variant="light">{t('agent.yangilangIzoh')}</Alert>
          ) : maktabsiz ? (
            <Alert color="gray" variant="light">{t('agent.maktabsiz')}</Alert>
          ) : buyruqBera ? (
            <Group gap="xs">
              {tugma('QAYTA_ISHGA_TUSHIR', <IconRotateClockwise size={14} />, 'red')}
              {a.pauza
                ? tugma('DAVOM', <IconPlayerPlay size={14} />, 'teal')
                : tugma('TOXTAT', <IconPlayerPause size={14} />, 'orange')}
              {tugma('YANGILA', <IconRefresh size={14} />, 'blue')}
              {tugma('LOG', <IconFileText size={14} />, 'gray')}
            </Group>
          ) : (
            <Text size="xs" c="dimmed">{t('agent.faqatStaff')}</Text>
          )}

          {/* Oxirgi vazifalar (10) */}
          <Box>
            <Text fw={600} size="sm" mb={6}>{t('agent.vazifalar')}</Text>
            {data.vazifalar.length === 0 ? (
              <Text size="sm" c="dimmed">{t('agent.vazifaYoq')}</Text>
            ) : (
              <Table.ScrollContainer minWidth={520}>
                <Table verticalSpacing={4} style={{ whiteSpace: 'nowrap' }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('agent.colTur')}</Table.Th>
                      <Table.Th>{t('agent.colHolat')}</Table.Th>
                      <Table.Th>{t('agent.colVaqt')}</Table.Th>
                      <Table.Th>{t('agent.colNatija')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.vazifalar.map((v) => (
                      <Table.Tr key={v.id}>
                        <Table.Td><Code>{v.kind}</Code></Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light" color={v.status === 'DONE' ? 'teal' : v.status === 'FAILED' ? 'red' : 'blue'} styles={BADGE_TOLIQ}>
                            {v.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDateTime(v.createdAt)}</Text></Table.Td>
                        <Table.Td><QisqaMatn v={v.error ?? v.result} max={60} rang={v.error ? 'red' : undefined} /></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Box>

          {/* Buyruqlar tarixi (20) */}
          <Box>
            <Text fw={600} size="sm" mb={6}>{t('agent.tarix')}</Text>
            {data.buyruqlar.length === 0 ? (
              <Text size="sm" c="dimmed">{t('agent.tarixYoq')}</Text>
            ) : (
              <Table.ScrollContainer minWidth={600}>
                <Table verticalSpacing={4} style={{ whiteSpace: 'nowrap' }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('agent.colTur')}</Table.Th>
                      <Table.Th>{t('agent.colHolat')}</Table.Th>
                      <Table.Th>{t('agent.colKim')}</Table.Th>
                      <Table.Th>{t('agent.colVaqt')}</Table.Th>
                      <Table.Th>{t('agent.colNatija')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.buyruqlar.map((b) => (
                      <Table.Tr key={b.id}>
                        <Table.Td><Text size="xs">{t(`agent.tur.${b.tur}`)}</Text></Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light" color={BUYRUQ_RANG[b.holat] ?? 'gray'} styles={BADGE_TOLIQ}>{t(`agent.buyruqHolat.${b.holat}`)}</Badge>
                        </Table.Td>
                        <Table.Td><Text size="xs">{b.kim ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{formatDateTime(b.createdAt)}</Text></Table.Td>
                        <Table.Td><QisqaMatn v={b.natija} max={50} rang={b.holat === 'XATO' || b.holat === 'MUDDATI_OTDI' ? 'red' : undefined} /></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Box>

          {/* Log — oxirgi LOG buyrug'ining matni */}
          <Box>
            <Group justify="space-between" mb={6}>
              <Text fw={600} size="sm">{t('agent.log')}</Text>
              {logData?.log && (
                <CopyButton value={logData.log}>
                  {({ copied, copy }) => (
                    <Button
                      size="compact-xs"
                      variant="light"
                      color={copied ? 'teal' : 'gray'}
                      onClick={copy}
                      leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                    >
                      {copied ? t('agent.nusxaOlindi') : t('agent.nusxaOl')}
                    </Button>
                  )}
                </CopyButton>
              )}
            </Group>
            {logKutish && (
              <Group gap={6} mb={6}>
                <Loader size="xs" />
                <Text size="xs" c="dimmed">{t('agent.logKutilmoqda')}</Text>
              </Group>
            )}
            {logId ? (
              logData?.log ? (
                <ScrollArea.Autosize mah={360} type="auto" data-testid="agent-log">
                  <Code block style={{ whiteSpace: 'pre', fontSize: 11 }}>{logData.log}</Code>
                </ScrollArea.Autosize>
              ) : logYuklanmoqda ? (
                <Loader size="xs" />
              ) : (
                <Text size="sm" c="dimmed">{t('agent.logBosh')}</Text>
              )
            ) : (
              !logKutish && <Text size="sm" c="dimmed">{t('agent.logYoq')}</Text>
            )}
          </Box>
        </Stack>
      )}
    </Drawer>
  );
}
